import { apiRequest } from './client';

// Enums
export enum UserProfileStatus {
  ONLINE = "online",
  BUSY = "busy",
  OFFLINE = "offline"
}

// Types
export interface UserProfile {
  user_id: number;
  display_name?: string;
  bio?: string;
  status?: UserProfileStatus;
  
  // Appearance
  age?: number;
  gender?: string;
  height?: number;
  weight?: number;
  ethnicity?: string;
  body_type?: string;
  hair_color?: string;
  hair_style?: string;
  eye_color?: string;
  
  // Personality & Interests
  personality_traits?: string[];
  interests?: string[];
  zodiac_sign?: string;
  
  // Background
  occupation?: string;
  education?: string;
  languages?: string[];
  
  // Preferences
  looking_for?: string;
  sexual_orientation?: string;
  smoking?: string;
  drinking?: string;

  // Onboarding
  onboarding_completed?: boolean;
  
  // Intimate (only for adult verified users)
  intimate_preferences?: string[];
  
  // Media
  avatar_url?: string;
  images?: string[];
  
  // Privacy & Visibility
  is_public: boolean;
  show_age: boolean;
  show_location: boolean;
  show_last_seen: boolean;
  
  // Statistics
  profile_views: number;
  profile_visits_today: number;
  completion_percentage: number;
  
  // Timestamps
  created_at?: string;
  updated_at?: string;
}

export interface UserProfileOptions {
  gender: string[];
  ethnicity: string[];
  body_type: string[];
  hair_color: string[];
  hair_style: string[];
  eye_color: string[];
  personality_traits: string[];
  interests: string[];
  zodiac_sign: string[];
  education: string[];
  languages: string[];
  looking_for: string[];
  sexual_orientation: string[];
  smoking: string[];
  drinking: string[];
  intimate_preferences: string[];
}

export interface UserProfileStats {
  profile_views: number;
  profile_visits_today: number;
  completion_percentage: number;
  last_updated?: string;
  is_public: boolean;
}

export interface UserProfileCreate {
  display_name?: string;
  bio?: string;
  status?: UserProfileStatus;
  age?: number;
  gender?: string;
  height?: number;
  weight?: number;
  ethnicity?: string;
  body_type?: string;
  hair_color?: string;
  hair_style?: string;
  eye_color?: string;
  personality_traits?: string[];
  interests?: string[];
  zodiac_sign?: string;
  occupation?: string;
  education?: string;
  languages?: string[];
  looking_for?: string;
  sexual_orientation?: string;
  smoking?: string;
  drinking?: string;
  intimate_preferences?: string[];
  avatar_url?: string;
  images?: string[];
  is_public?: boolean;
  show_age?: boolean;
  show_location?: boolean;
  show_last_seen?: boolean;
}

export type UserProfileUpdate = Partial<UserProfileCreate>;

// API Functions
export const userProfileApi = {
  // Get my profile
  getMyProfile: async (): Promise<UserProfile> => {
    return apiRequest('/profile/');
  },

  // Update my profile
  updateMyProfile: async (data: UserProfileUpdate): Promise<UserProfile> => {
    return apiRequest('/profile/', {
      method: 'PUT',
      data,
    });
  },

  // Get profile options
  getProfileOptions: async (): Promise<UserProfileOptions> => {
    return apiRequest('/profile/options');
  },

  // Get profile stats
  getProfileStats: async (): Promise<UserProfileStats> => {
    return apiRequest('/profile/stats');
  },

  // Upload avatar
  uploadAvatar: async (file: File): Promise<{ avatar_url: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    return apiRequest('/profile/upload-avatar', {
      method: 'POST',
      data: formData,
      headers: {}, // Let browser set content-type for FormData
    });
  },

  // Upload additional images
  uploadImages: async (files: File[]): Promise<{ images: Array<{ url: string; thumbnail_url?: string }>; total: number; limit: number }> => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    return apiRequest('/profile/upload-images', {
      method: 'POST',
      data: formData,
      headers: {}, // Let browser set content-type for FormData
    });
  },

  // List my gallery images
  listGallery: async (): Promise<{ images: Array<{ url: string; thumbnail_url?: string }>; total: number; limit: number }> => {
    return apiRequest('/profile/gallery');
  },

  // Delete one gallery image by index
  deleteGalleryImage: async (index: number): Promise<{ success: boolean }> => {
    return apiRequest(`/profile/gallery/${index}`, { method: 'DELETE' });
  },

  // Soft-delete my account
  deleteMyAccount: async (): Promise<{ message: string }> => {
    return apiRequest('/users/me', { method: 'DELETE' });
  },

  // Get another user's profile (public only)
  getUserProfile: async (userId: number): Promise<UserProfile> => {
    return apiRequest(`/profile/${userId}`);
  },

  // Complete onboarding
  completeOnboarding: async (): Promise<{ success: boolean; onboarding_completed: boolean }> => {
    return apiRequest('/profile/complete-onboarding', { method: 'POST' });
  },
};